import { BugsnagErrorHandler } from "@bugsnag/plugin-angular";
import { Capacitor } from "@capacitor/core";

const RELOAD_MARKER_KEY = "apoapp-chunk-reload";

/**
 * Cooldown between two recovery reloads. Long enough that a genuinely missing
 * chunk cannot put the page into a reload loop, short enough that a user who
 * hits the problem again after a later deploy still recovers automatically.
 */
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Browsers word this differently, and the wording has changed across versions,
 * so match all of the known variants.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk \S+ failed/i;

/**
 * A reload tears the page down asynchronously, so more chunk errors can arrive
 * in the meantime. They must not be reported - the recovery is already running.
 */
let reloadScheduled = false;

export function isChunkLoadError(error: unknown, depth = 0): boolean {
  if (error == null || depth > 3) {
    return false;
  }

  if (typeof error === "string") {
    return CHUNK_LOAD_ERROR_PATTERN.test(error);
  }

  const candidate = error as {
    message?: unknown;
    rejection?: unknown;
    cause?: unknown;
    originalError?: unknown;
  };

  if (
    typeof candidate.message === "string" &&
    CHUNK_LOAD_ERROR_PATTERN.test(candidate.message)
  ) {
    return true;
  }

  // Zone.js and Angular hand the ErrorHandler a wrapper around the real
  // rejection reason often enough that the original has to be unwrapped.
  return (
    isChunkLoadError(candidate.rejection, depth + 1) ||
    isChunkLoadError(candidate.cause, depth + 1) ||
    isChunkLoadError(candidate.originalError, depth + 1)
  );
}

/**
 * The app is served as hashed files and the Capacitor plugins pull their web
 * implementations in through dynamic `import()`. Those chunks are only fetched
 * the first time a plugin is used, so a client that loaded `index.html` and
 * `main-*.js` before a deploy can ask for a chunk that the new image no longer
 * contains - during a rolling update the same client can even be served the old
 * document and the new assets. The request 404s and the import rejects with
 * "Failed to fetch dynamically imported module".
 *
 * Nothing in the app can recover that chunk, but reloading picks up a
 * consistent set of assets, so treat it as a reload signal instead of a crash
 * and only report it to Bugsnag if reloading did not help.
 */
export class ChunkLoadErrorHandler extends BugsnagErrorHandler {
  override handleError(error: any): void {
    if (isChunkLoadError(error) && this.tryReload()) {
      return;
    }

    super.handleError(error);
  }

  /** @returns whether the error was absorbed by a reload. */
  private tryReload(): boolean {
    // Native builds ship their assets inside the app bundle, so a missing chunk
    // there is a real packaging bug and reloading would only hide it.
    if (Capacitor.getPlatform() !== "web") {
      return false;
    }

    if (reloadScheduled) {
      return true;
    }

    try {
      const lastReload = Number(sessionStorage.getItem(RELOAD_MARKER_KEY));

      if (lastReload && Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
        return false;
      }

      sessionStorage.setItem(RELOAD_MARKER_KEY, `${Date.now()}`);
    } catch (e) {
      // Storage can be unavailable (private mode, blocked cookies). Without it
      // there is no loop protection, so report instead of reloading.
      console.warn("Could not record the chunk reload marker", e);
      return false;
    }

    reloadScheduled = true;
    console.warn("A lazily loaded chunk is missing, reloading the app");
    window.location.reload();

    return true;
  }
}
